import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
  AppBar,
  Toolbar,
  Avatar,
  Stack,
  Dialog,
  DialogContent,
  Chip,
  CircularProgress,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HistoryIcon from '@mui/icons-material/History';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LogoutIcon from '@mui/icons-material/Logout';
import EventIcon from '@mui/icons-material/Event';
import InfoIcon from '@mui/icons-material/Info';
import DescriptionIcon from '@mui/icons-material/Description';
import { removeUserData, getUserData } from '../../services/storageService';
import { getMyAgreements } from '../../services/agreementService';
import NotificationSystem from './NotificationSystem';

const drawerWidth = 280;

const Sidebar = ({ user, children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [agreementDialog, setAgreementDialog] = useState(false);
  const [myAgreements, setMyAgreements] = useState([]);
  const [agreementsLoading, setAgreementsLoading] = useState(false);
  const [viewingAgreement, setViewingAgreement] = useState(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    removeUserData();
    navigate('/');
  };

  const handleOpenAgreements = async () => {
    setAgreementDialog(true);
    setAgreementsLoading(true);
    try {
      const res = await getMyAgreements();
      if (res.success) setMyAgreements(res.data || []);
    } catch (e) { console.error(e); }
    finally { setAgreementsLoading(false); }
  };

  const API_URL = process.env.REACT_APP_API_URL || 'https://backend-c-0mxm.onrender.com/api';
  const token = getUserData()?.userId || getUserData()?.token;

  const menuItems = [
    {
      text: 'Chấm công',
      icon: <DashboardIcon sx={{ color: 'white' }} />,
      path: '/dashboard'
    },
    {
      text: 'Lịch sử',
      icon: <HistoryIcon sx={{ color: 'white' }} />,
      path: '/dashboard/history'
    },
    {
      text: 'Đăng ký livestream',
      icon: <EventIcon sx={{ color: 'white' }} />,
      path: '/dashboard/schedule'
    },
    {
      text: 'Báo cáo',
      icon: <AssessmentIcon sx={{ color: 'white' }} />,
      path: '/dashboard/reports'
    },
    {
      text: 'Giới thiệu',
      icon: <InfoIcon sx={{ color: 'white' }} />,
      path: '/about'
    },
    {
      text: 'Điều khoản & HĐ',
      icon: <DescriptionIcon sx={{ color: 'white' }} />,
      action: handleOpenAgreements,
    }
  ];

  const drawer = (
    <Box sx={{ height: '100%', background: '#1e293b' }}>
      <Box sx={{ 
        p: 3, 
        display: 'flex', 
        alignItems: 'center',
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
      }}>
        <Typography variant="h5" sx={{ 
          color: '#e31e24',
          fontWeight: 'bold',
          letterSpacing: 1,
        }}>
          CARNAVAL
        </Typography>
      </Box>
      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
      <List sx={{ p: 2 }}>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => item.action ? item.action() : navigate(item.path)}
            sx={{
              borderRadius: 2,
              mb: 1,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              bgcolor: location.pathname === item.path ? 'rgba(255,255,255,0.1)' : 'transparent'
            }}
          >
            <ListItemIcon>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} sx={{ color: 'white' }} />
          </ListItem>
        ))}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
      <List sx={{ p: 2 }}>
        <ListItem button 
          onClick={handleLogout}
          sx={{ 
            borderRadius: 2,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <ListItemIcon>
            <LogoutIcon sx={{ color: 'white' }} />
          </ListItemIcon>
          <ListItemText primary="Đăng xuất" sx={{ color: 'white' }} />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: '#f5f8fa', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'white',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: '#1e293b' }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="subtitle1" sx={{ color: '#1e293b', whiteSpace: 'nowrap' }}>
              {user?.fullName}
            </Typography>
            <NotificationSystem page="global" maxNotifications={5} refreshInterval={30000} />
            <Avatar sx={{ bgcolor: '#e31e24' }}>{user?.fullName?.[0]}</Avatar>
          </Stack>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: drawerWidth,
                bgcolor: '#1e293b',
              },
            }}
          >
            {drawer}
          </Drawer>
        ) : (
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: drawerWidth,
                bgcolor: '#1e293b',
                border: 'none',
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        )}
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 3 },
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          maxWidth: '100vw',
          overflowX: 'hidden',
          mt: '64px',
        }}
      >
        {children}
      </Box>

      {/* Agreements list dialog */}
      <Dialog open={agreementDialog} onClose={() => { setAgreementDialog(false); setViewingAgreement(null); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #059669 100%)', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DescriptionIcon sx={{ color: 'white', fontSize: 28 }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, flex: 1 }}>Điều khoản & Hợp đồng</Typography>
          <IconButton onClick={() => { setAgreementDialog(false); setViewingAgreement(null); }} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ px: 3, py: 2 }}>
          {agreementsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : myAgreements.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Chưa có điều khoản nào</Typography>
          ) : (
            <Stack spacing={1.5}>
              {myAgreements.map(ag => (
                <Box key={ag._id} onClick={() => setViewingAgreement(ag)} sx={{
                  p: 2, borderRadius: 2, border: '1px solid #e2e8f0', cursor: 'pointer',
                  transition: 'all 0.2s', '&:hover': { borderColor: '#2563eb', bgcolor: '#f8fafc' },
                }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PictureAsPdfIcon sx={{ color: '#dc2626', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>{ag.title}</Typography>
                    <Chip label={`v${ag.version}`} size="small" sx={{ fontWeight: 700, bgcolor: '#e0f2fe', color: '#0369a1', height: 22 }} />
                    {ag.accepted ? (
                      <Chip icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label="Đã đồng ý" size="small"
                        sx={{ fontWeight: 600, bgcolor: '#dcfce7', color: '#16a34a', height: 22, '& .MuiChip-icon': { color: '#16a34a' } }} />
                    ) : (
                      <Chip label="Chưa đồng ý" size="small" sx={{ fontWeight: 600, bgcolor: '#fef3c7', color: '#92400e', height: 22 }} />
                    )}
                  </Stack>
                  {ag.description && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{ag.description}</Typography>}
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* View agreement PDF dialog */}
      <Dialog open={!!viewingAgreement} onClose={() => setViewingAgreement(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', height: '85vh', maxHeight: 700 } }}>
        {viewingAgreement && (
          <>
            <Box sx={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #059669 100%)', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PictureAsPdfIcon sx={{ color: 'white', fontSize: 24 }} />
              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700, flex: 1 }}>{viewingAgreement.title}</Typography>
              <Chip label={`v${viewingAgreement.version}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }} />
              <IconButton onClick={() => setViewingAgreement(null)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
            </Box>
            <Box sx={{ flex: 1 }}>
              <iframe
                src={`${API_URL}/agreements/${viewingAgreement._id}/file?token=${token}`}
                title={viewingAgreement.title}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            </Box>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Sidebar; 